import { useInfoMessage } from "hooks";
import useOneRepMaxActions from "providers/OneRepMaxProvider/useOneRepMaxActions";
import { useCallback, useState } from "react";
import { Exercise } from "types";
import { OneRepMaxValue } from "types/onerepmax";
import { FormOneRepMax } from "../useOneRepMaxFormInitialValues/useOneRepMaxFormInitialValues.types";
import OneRepMaxBulkEditFormHelpers from "helpers/OneRepMaxBulkEditFormHelpers";
import usePreferences from "hooks/usePreferences";
import { useTranslation } from "react-i18next";

interface UseBulkOneRepMaxUpdateProps {
  exercise: Exercise | null;
}

function useBulkOneRepMaxUpdate(props: UseBulkOneRepMaxUpdateProps) {
  const { exercise } = props;
  const oneRepMaxActions = useOneRepMaxActions();
  const successMessage = useInfoMessage({ type: "success" });
  const errorMessage = useInfoMessage({ type: "error" });
  const [{ imperialSystemEnabled }] = usePreferences();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const updateOneRepMaxes = useCallback(
    async (values: { athletesOneRepMaxes: FormOneRepMax[] }) => {
      setLoading(true);
      try {
        const updatedOneRepMaxes: {
          athleteId: string;
          oneRepMaxes: Pick<OneRepMaxValue, "exerciseId" | "weight" | "variant">[];
        }[] = values.athletesOneRepMaxes
          .map((formOneRepMax) => OneRepMaxBulkEditFormHelpers.fromFormRow(formOneRepMax, { imperialSystemEnabled }))
          .map(({ oneRepMaxes, ...rest }) => ({ ...rest, oneRepMaxes: oneRepMaxes.filter(({ weight }) => weight) }))
          .filter((oneRepMax) => oneRepMax.oneRepMaxes.length);

        if (updatedOneRepMaxes.length) {
          await oneRepMaxActions.updateByAthleteIds(`${exercise?.id}`, updatedOneRepMaxes);
          successMessage({ message: t("workouts.one_rep_max_values_submitted") });
        }
      } catch (error) {
        errorMessage({ message: t("errors.failed_to_submit_1rm_values") });
      } finally {
        setLoading(false);
      }
    },
    [t, imperialSystemEnabled, oneRepMaxActions, exercise?.id, successMessage, errorMessage]
  );

  return { updateOneRepMaxes, loading };
}

export default useBulkOneRepMaxUpdate;
